import React, { useState } from 'react';
import { ArrowRight, Bot, Megaphone, TrendingUp, Headphones, Zap, Users, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { usePreApproval } from '../../pages/LandingPage';

const GrantShowcase = () => {
  const [activeCategory, setActiveCategory] = useState(null);
  const { openPreApproval } = usePreApproval();

  const categories = [
    {
      id: 'ai',
      name: 'AI Tools',
      icon: Bot,
      color: '#0891b2', // deeper cyan
      gradientStart: '#06b6d4',
      gradientEnd: '#0e7490',
      percentage: 25,
      items: [
        'Unlimited AI Chat Bots',
        'AI Text Agents',
        'AI Voice Agents',
        'Unlimited AI Knowledge Bases',
        'AI Powered CRM'
      ]
    },
    {
      id: 'sales',
      name: 'Sales',
      icon: TrendingUp,
      color: '#d97706', // deeper amber
      gradientStart: '#f59e0b',
      gradientEnd: '#b45309',
      percentage: 20,
      items: [
        'Live Lead Engine - 100s of leads/day',
        'BDC-X Call Center',
        'Client Success Portal',
        'Paid Ads Team'
      ]
    },
    {
      id: 'marketing',
      name: 'Marketing',
      icon: Megaphone,
      color: '#7c3aed', // deeper violet
      gradientStart: '#a78bfa',
      gradientEnd: '#6d28d9',
      percentage: 20,
      items: [
        'Cinematic Ad System',
        'Short Film Commercials',
        'Paid Ads Teams (Meta, Google, TikTok)',
        'TV, Radio & Billboards',
        'Direct Mail Campaigns'
      ]
    },
    {
      id: 'automation',
      name: 'Automations',
      icon: Zap,
      color: '#16a34a', // deeper green
      gradientStart: '#4ade80',
      gradientEnd: '#15803d',
      percentage: 15,
      items: [
        'Marketing Automation',
        'Reputation Management',
        '5 Star Reviews on Autopilot',
        'SEO Domination'
      ]
    },
    {
      id: 'support',
      name: 'Support',
      icon: Headphones,
      color: '#db2777', // deeper pink
      gradientStart: '#f472b6',
      gradientEnd: '#be185d',
      percentage: 10,
      items: [
        'Human Directed Support',
        'Premium CRM Snapshots',
        'Best of Best Listing Management',
        '40 Virtual Teams On Demand'
      ]
    },
    {
      id: 'extras',
      name: 'Bonus Tools',
      icon: Users,
      color: '#ea580c', // deeper orange
      gradientStart: '#fb923c',
      gradientEnd: '#c2410c',
      percentage: 10,
      items: [
        'Google Maps Ranker & Map Stacking',
        'Custom Affiliate Program',
        'Free Incentives Vault',
        'Unlimited Orders Covered'
      ]
    }
  ];

  // Calculate donut segments
  const calculateDonutSegments = () => {
    let cumulativePercentage = 0;
    return categories.map((cat) => {
      const startAngle = (cumulativePercentage / 100) * 360;
      cumulativePercentage += cat.percentage;
      const endAngle = (cumulativePercentage / 100) * 360;
      return { ...cat, startAngle, endAngle };
    });
  };

  const segments = calculateDonutSegments();

  // SVG arc path calculator
  const describeArc = (cx, cy, outerR, innerR, startAngle, endAngle) => {
    const start = polarToCartesian(cx, cy, outerR, endAngle);
    const end = polarToCartesian(cx, cy, outerR, startAngle);
    const innerStart = polarToCartesian(cx, cy, innerR, endAngle);
    const innerEnd = polarToCartesian(cx, cy, innerR, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    return [
      "M", start.x, start.y,
      "A", outerR, outerR, 0, largeArcFlag, 0, end.x, end.y,
      "L", innerEnd.x, innerEnd.y,
      "A", innerR, innerR, 0, largeArcFlag, 1, innerStart.x, innerStart.y,
      "Z"
    ].join(" ");
  };

  const polarToCartesian = (cx, cy, r, angle) => {
    const rad = (angle - 90) * Math.PI / 180;
    return {
      x: cx + r * Math.cos(rad),
      y: cy + r * Math.sin(rad)
    };
  };

  // Calculate icon position at the middle of each segment
  const getIconPosition = (startAngle, endAngle) => {
    const midAngle = (startAngle + endAngle) / 2;
    const radius = 70; // Position icons in the middle of the donut ring
    return polarToCartesian(100, 100, radius, midAngle);
  };

  return (
    <section className="py-20 md:py-28 bg-slate-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />
        <div className="absolute top-1/4 left-0 w-1/3 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-1/3 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center space-x-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-2 mb-6">
            <span className="text-emerald-400 text-sm font-medium">$120,000 Co-Op Funding</span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            Your Business Deserves
            <span className="text-emerald-400 block">AI Horse-Powered Growth</span>
          </h2>

          <p className="text-lg text-slate-400 max-w-3xl mx-auto leading-relaxed">
            The $120K Co-Op Funding covers your complete AI-powered business system — 
            replacing agencies, lead vendors, and consultants with one unified platform.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-12">
          {/* Donut Chart */}
          <div className="relative flex justify-center items-center order-2 lg:order-1">
            <div className="relative w-[320px] h-[320px] sm:w-[380px] sm:h-[380px]">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 rounded-full blur-3xl scale-110" />
              
              {/* SVG Donut */}
              <svg viewBox="0 0 200 200" className="w-full h-full relative z-10 drop-shadow-2xl">
                {/* Define gradients */}
                <defs>
                  {segments.map((segment) => (
                    <linearGradient 
                      key={`gradient-${segment.id}`} 
                      id={`gradient-${segment.id}`}
                      x1="0%" y1="0%" x2="100%" y2="100%"
                    >
                      <stop offset="0%" stopColor={segment.gradientStart} />
                      <stop offset="100%" stopColor={segment.gradientEnd} />
                    </linearGradient>
                  ))}
                </defs>
                
                {/* Outer black ring */}
                <circle cx="100" cy="100" r="94" fill="none" stroke="#000000" strokeWidth="4" />
                <circle cx="100" cy="100" r="91" fill="none" stroke="#1e293b" strokeWidth="2" />
                
                {segments.map((segment, index) => (
                  <path
                    key={segment.id}
                    d={describeArc(100, 100, 88, 50, segment.startAngle, segment.endAngle - 1.5)}
                    fill={`url(#gradient-${segment.id})`}
                    stroke="#000000"
                    strokeWidth="1.5"
                    className="cursor-pointer"
                    style={{
                      filter: activeCategory === segment.id ? 'brightness(1.25) drop-shadow(0 0 8px ' + segment.color + ')' : 'none',
                      transition: 'filter 0.2s ease'
                    }}
                    onMouseEnter={() => setActiveCategory(segment.id)}
                    onMouseLeave={() => setActiveCategory(null)}
                  />
                ))}
                
                {/* Inner black ring */}
                <circle cx="100" cy="100" r="50" fill="none" stroke="#000000" strokeWidth="3" />
                
                {/* Center Circle */}
                <circle cx="100" cy="100" r="46" fill="#0f172a" />
                <circle cx="100" cy="100" r="44" fill="none" stroke="#334155" strokeWidth="1" />
                <text x="100" y="94" textAnchor="middle" className="fill-white text-[10px] font-bold tracking-wide">
                  ALL-IN-ONE
                </text>
                <text x="100" y="108" textAnchor="middle" className="fill-amber-400 text-[8px] font-semibold tracking-wider">
                  PLATFORM
                </text>
              </svg>

              {/* Icons positioned on each segment */}
              {segments.map((segment) => {
                const Icon = segment.icon;
                const pos = getIconPosition(segment.startAngle, segment.endAngle);
                const leftPercent = (pos.x / 200) * 100;
                const topPercent = (pos.y / 200) * 100;
                
                return (
                  <div
                    key={`icon-${segment.id}`}
                    className="absolute z-20 pointer-events-none"
                    style={{
                      left: `${leftPercent}%`,
                      top: `${topPercent}%`,
                      transform: 'translate(-50%, -50%)'
                    }}
                  >
                    <div 
                      className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center shadow-lg border-2 border-black"
                      style={{ 
                        background: `linear-gradient(135deg, ${segment.gradientStart}, ${segment.gradientEnd})`,
                        boxShadow: `0 0 12px ${segment.color}60`
                      }}
                    >
                      <Icon 
                        className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-white" 
                      />
                    </div>
                  </div>
                );
              })}

              {/* Floating Badge */}
              <div className="absolute -top-2 -right-2 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white font-bold px-3 py-1.5 rounded-full text-xs shadow-lg shadow-cyan-500/40 z-30 border border-black">
                Limited Spots
              </div>
            </div>
          </div>

          {/* Category Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-3 order-1 lg:order-2">
            {categories.map((category) => {
              const Icon = category.icon;
              const isActive = activeCategory === category.id;
              
              return (
                <div
                  key={category.id}
                  className={`relative p-4 rounded-xl border cursor-pointer group ${
                    isActive 
                      ? 'bg-slate-800/80 border-slate-600' 
                      : 'bg-slate-800/40 border-slate-700/50 hover:bg-slate-800/60 hover:border-slate-600'
                  }`}
                  style={{
                    boxShadow: isActive ? `0 0 20px ${category.color}30` : 'none',
                    transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease'
                  }}
                  onMouseEnter={() => setActiveCategory(category.id)}
                  onMouseLeave={() => setActiveCategory(null)}
                >
                  {/* Color accent bar with gradient */}
                  <div 
                    className="absolute top-0 left-0 right-0 h-1 rounded-t-xl"
                    style={{ background: `linear-gradient(90deg, ${category.gradientStart}, ${category.gradientEnd})` }}
                  />
                  
                  <div className="flex items-center gap-3 mb-2">
                    <div 
                      className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ background: `linear-gradient(135deg, ${category.gradientStart}30, ${category.gradientEnd}30)` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: category.gradientStart }} />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-sm">{category.name}</h4>
                      <p className="text-slate-500 text-xs">{category.percentage}% of platform</p>
                    </div>
                  </div>

                  {/* Items Preview */}
                  <div className={`overflow-hidden transition-all duration-300 ${isActive ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <ul className="space-y-1 pt-2 border-t border-slate-700/50">
                      {category.items.slice(0, 4).map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-xs text-slate-400">
                          <ChevronRight className="w-3 h-3" style={{ color: category.gradientStart }} />
                          <span className="truncate">{item}</span>
                        </li>
                      ))}
                      {category.items.length > 4 && (
                        <li className="text-xs text-slate-500 pl-5">+{category.items.length - 4} more</li>
                      )}
                    </ul>
                  </div>

                  {/* Hover hint */}
                  <p className={`text-xs text-slate-500 mt-2 transition-opacity ${isActive ? 'opacity-0 h-0' : 'opacity-100'}`}>
                    Hover to see tools
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom Stats & CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10">
          <div className="flex items-center gap-8 text-center">
            <div>
              <p className="text-3xl font-bold text-white">1000+</p>
              <p className="text-xs text-slate-500">Tools Included</p>
            </div>
            <div className="w-px h-10 bg-slate-700" />
            <div>
              <p className="text-3xl font-bold text-emerald-400">$120K</p>
              <p className="text-xs text-slate-500">Grant Value</p>
            </div>
            <div className="w-px h-10 bg-slate-700" />
            <div>
              <p className="text-3xl font-bold text-cyan-400">1</p>
              <p className="text-xs text-slate-500">Platform</p>
            </div>
          </div>

          <Button
            onClick={openPreApproval}
            size="lg"
            data-testid="grant-showcase-cta-btn"
            className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-slate-950 font-semibold px-8 py-6 text-lg rounded-xl transition-all hover:shadow-xl hover:shadow-emerald-500/25 group"
          >
            Check Your Eligibility
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default GrantShowcase;
